import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, Signal, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LocalUserRoles } from '@buspro/shared/common';
import {
  ApproachingBusesWidgetComponent,
  AssignedLocationsCardComponent,
  DailyStatsCardComponent,
  LiveBookingModalComponent,
  RecentBookingsTableComponent,
} from '@buspro/web/admin/shared';
import {
  ApproachingBusDto,
  AssignedLocationDto,
  BusproAdminLiveBookingsApi,
  LocalUserDto,
  MyDashboardDto,
} from '@buspro/web/apis';
import { OperationsLocationWebSocketService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-bus-stop-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    LiveBookingModalComponent,
    ApproachingBusesWidgetComponent,
    DailyStatsCardComponent,
    AssignedLocationsCardComponent,
    RecentBookingsTableComponent,
  ],
  templateUrl: './bus-stop-dashboard.component.html',
})
export class BusStopDashboardComponent implements OnInit, OnDestroy {
  private wsService = inject(OperationsLocationWebSocketService);
  private api = inject(BusproAdminLiveBookingsApi);
  private router = inject(Router);
  private ngxAuthService = inject(NgxAuthService);
  private logger = inject(NGXLogger);

  // Convert Observable to Signal for easier access
  authUser: Signal<LocalUserDto | null> = toSignal(this.ngxAuthService.state, { initialValue: null });

  showBookingModal = signal(false);
  selectedBusForBooking = signal<any>(null);

  myDashboardQuery = injectQuery(() => ({
    queryKey: ['bus-stop-dashboard'],
    queryFn: () => lastValueFrom(this.api.busproManagedLiveBookingsControllerGetMyDashboard()),
    refetchInterval: 30000, // Refresh every 30 seconds
  }));

  dashboardData = computed(() => this.myDashboardQuery.data() || (null as MyDashboardDto | null));

  assignedLocations = computed(() => this.dashboardData()?.assignedLocations || ([] as AssignedLocationDto[]));
  approachingBuses = computed(() => this.dashboardData()?.approachingBuses || ([] as ApproachingBusDto[]));
  approachingBusesSignal = signal<ApproachingBusDto[]>([]);
  isLoadingSignal = signal(false);
  hasErrorSignal = signal(false);
  recentBookings = computed(() => this.dashboardData()?.recentBookings || []);
  statistics = computed(() => this.dashboardData()?.statistics);
  selectedLocationId = signal<string | null>(null);

  constructor() {
    // Update approaching buses signal when data changes
    effect(() => {
      const buses = this.approachingBuses();
      this.approachingBusesSignal.set(buses);
    });

    // Update loading and error states
    effect(() => {
      this.isLoadingSignal.set(this.myDashboardQuery.isFetching());
      this.hasErrorSignal.set(this.myDashboardQuery.isError());
    });

    // Subscribe to WebSocket updates when dashboard data changes
    effect(() => {
      const locations = this.assignedLocations();
      if (locations.length > 0 && this.wsService.isConnected) {
        locations.forEach((location: AssignedLocationDto) => {
          this.wsService.subscribeToLocation(location.id, location.type);
        });
      }
    });
  }

  getLocationCenter(): google.maps.LatLngLiteral | null {
    const firstLocation = this.assignedLocations()[0];
    if (firstLocation?.coordinates) {
      return {
        lat: firstLocation.coordinates.lat,
        lng: firstLocation.coordinates.lng,
      };
    }
    return null;
  }

  ngOnInit(): void {
    // Check if user is NOT a bus stop staff - redirect them to main dashboard
    const currentUser = this.authUser();
    if (currentUser) {
      const role = currentUser.role;
      if (role !== LocalUserRoles.BUSPRO_BUS_STOP_MANAGER && role !== LocalUserRoles.BUSPRO_BUS_STOP_CLERK) {
        this.logger.info('Non-bus-stop staff detected, redirecting to main dashboard');
        this.router.navigateByUrl('/dashboard');
        return;
      }
    }

    // Connect WebSocket
    this.wsService.connect();
  }

  ngOnDestroy(): void {
    // Unsubscribe from all locations
    const locations = this.assignedLocations();
    locations.forEach((location: AssignedLocationDto) => {
      this.wsService.unsubscribeFromLocation(location.id);
    });
    this.wsService.disconnect();
  }

  openBookingModal(bus: any): void {
    // Get the first assigned location for booking
    const firstLocation = this.assignedLocations()[0];
    if (!firstLocation) {
      console.error('No assigned locations found');
      return;
    }
    this.selectedBusForBooking.set(bus);
    this.showBookingModal.set(true);
  }

  closeBookingModal(): void {
    this.showBookingModal.set(false);
    this.selectedBusForBooking.set(null);
  }

  getBookingLocationId(): string {
    const firstLocation = this.assignedLocations()[0];
    return firstLocation?.id || '';
  }

  getBookingLocationType(): string {
    const firstLocation = this.assignedLocations()[0];
    return firstLocation?.type || 'STANDARD_PLACE';
  }

  onBookingSuccess(): void {
    // Refetch dashboard data after successful booking
    this.myDashboardQuery.refetch();
    this.closeBookingModal();
  }

  refreshData(): void {
    this.myDashboardQuery.refetch();
  }

  onLocationSelected(locationId: string): void {
    this.selectedLocationId.set(locationId);
    // Optionally filter approaching buses by location
    // For now, just update the selected location
  }
}
